import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useHospitalContext } from './HospitalContextApi';
import { requireProviderDocumentsByExtraIds } from '../../store/providers/providerSlice';
import { appDispatch, RootState } from 'store/store';
import { addResponse, updateResponse } from '../../store/survey/surveySlice';
import {
  IAnswersView,
  IMultipleAnswerAnswerView,
  IMultipleAnswerQuestionView,
  ISurveyItem,
  ISurveyResponseView,
  ISurveyViewServer,
  PeriodType,
} from '../../store/survey/surveyTypes';
import { useSelector } from 'react-redux';
import { provider_getDocument } from '../../api/ManApi';
import { getSurvey } from '../../api/SurveyApi';
import cloneDeep from 'lodash/cloneDeep';
import { BASE_URL } from '../../api/chatbot-api/SendMessageWithThread';

interface IProps {
  children: React.ReactNode;
  providerId: number;
}

interface IQuestion {
  content: string;
  streamAnswer: string;
  isLoading: boolean;
}

export interface IQuestionListItem {
  threadId: string;
  title: string;
  ans: string;
  annotations: any;
  rating: 0 | 1 | null;
  key: string;
}

type IKeys = 'sender' | 'message' | 'messagetext' | 'userrate';

export interface IComponentValues {
  question: IQuestion;
  setQuestion: (question: IQuestion) => void;
  questionList: IQuestionListItem[];
  pauseMessage: (message: string) => void;
  handleChangeRating: (index: string, rating: 0 | 1) => void;
}

function markdownToText(markdown: string) {
  return markdown
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[#_*`~>+-]/g, '')
    .replace(/\n+/g, ' ')
    .trim();
}

const cntx = createContext<IComponentValues | null>(null);

const ContextProvider: React.FC<IProps> = ({ providerId, children }) => {
  const hospital = useHospitalContext();
  const [question, setQuestion] = useState<IQuestion>({
    content: '',
    streamAnswer: '',
    isLoading: false,
  });

  const state = useSelector((state: RootState) => state);

  const [questionList, setQuestionList] = useState<IQuestionListItem[]>([]);
  const [thread, setThread] = useState('');
  const [fetchingData, setFetchingData] = useState<boolean>(false);
  const [responseItem, setResponseItem] = useState<ISurveyResponseView | null>(
    null
  );
  const [responseCounter, setResponseCounter] = useState<number>(0);

  const abortControllerRef = useRef<AbortController | null>(null);
  const isRunningBot = useRef<boolean>(false);
  const survey = useRef<ISurveyViewServer | null>(null);
  const surveyItem = useRef<ISurveyItem | null>(null);

  const { t } = useTranslation('chatbot');

  let timer: any = null;
  const listeningForResponse = useRef<boolean>(false);

  const saveChatContent = surveyItem.current;

  const generateKeys = (name: IKeys) => {
    const question = survey.current?.surveyQuestionList.find(
      (item) => item.type === 'multipleAnswer'
    ) as IMultipleAnswerQuestionView;
    const key = question!.questionFields.find(
      (el) => el.title!.toLowerCase() === name
    )!.key;
    return key;
  };

  const generateSubAnswerList = () => {
    const lastResponse = questionList.slice(-1)[0];

    const botKey = lastResponse.key;
    const userKey = `${Math.round(Math.random() * 1000_000)}`;

    const sendarKey = generateKeys('sender');
    const messageTextKey = generateKeys('messagetext');
    const messageKey = generateKeys('message');
    const userRateKey = generateKeys('userrate');
    setQuestionList((prevState) => [
      ...prevState.slice(0, -1),
      { ...prevState[prevState.length - 1], key: botKey },
    ]);

    return [
      // for User
      {
        implicit: false,
        order: responseCounter,
        ref: userKey,
        values: [
          { key: sendarKey, value: 'User' },
          { key: messageKey, value: `${lastResponse.title}` },
          {
            key: messageTextKey,
            value: `${markdownToText(lastResponse.title)}`,
          },
          { key: userRateKey, value: '' },
        ],
      },
      // for Bot
      {
        implicit: false,
        order: responseCounter + 1,
        ref: botKey,
        values: [
          { key: sendarKey, value: 'Bot' },
          { key: messageKey, value: `${lastResponse.ans}` },
          { key: messageTextKey, value: `${markdownToText(lastResponse.ans)}` },
          { key: userRateKey, value: '' },
        ],
      },
    ];
  };

  const generateAnswerList = () => {
    const multipleAnswerQuestion = survey.current?.surveyQuestionList.find(
      (item) => item.type === 'multipleAnswer'
    ) as IMultipleAnswerQuestionView;
    const textQuestion = survey.current?.surveyQuestionList.find(
      (item) => item.type === 'text'
    );
    const subAnswerList = generateSubAnswerList();
    return [
      {
        questionId: multipleAnswerQuestion!.id,
        subAnswerList: [...subAnswerList],
        type: 'multipleAnswer',
      },
      {
        value: thread,
        type: 'text',
        questionId: textQuestion!.id,
      },
    ];
  };

  const handleUpdateAddResponse = (
    type: 'add' | 'update',
    response: ISurveyResponseView,
    surveyItemData: ISurveyItem,
    periodType: PeriodType = 'day',
    dontSendNotification: boolean = true
  ) => {
    if (type === 'add') {
      appDispatch(
        addResponse({
          response: response,
          surveyItemData: surveyItem.current as ISurveyItem,
          periodType: periodType,
        })
      ).then((res: any) => {
        setResponseItem(res.response.data);
      });
    } else {
      appDispatch(
        updateResponse({
          response: response as ISurveyResponseView,
          surveyItemData: surveyItemData as ISurveyItem,
          dontSendNotification: dontSendNotification,
        })
      );
    }
  };

  useEffect(() => {
    if (hospital?.hospitalInitialInformation.chatHistoryItemId) {
      provider_getDocument(
        hospital?.hospitalInitialInformation.chatHistoryItemId,
        state,
        hospital?.hospitalInitialInformation.chatHistoryItemViewToken
      ).then((res) => {
        surveyItem.current = res.data;
        getSurvey(
          res.data.changeDataToken || res.data.fillToken,
          undefined,
          undefined,
          undefined
        ).then((res) => {
          survey.current = res.data;
        });
      });
    } else {
      survey.current = null;
      surveyItem.current = null;
    }
  }, [hospital?.hospitalInitialInformation.chatHistoryItemId]);

  useEffect(() => {
    if (questionList.slice(-1).length > 0 && saveChatContent) {
      if (questionList.length > 1) {
        // update
        const clonedResponse = cloneDeep(responseItem);
        (clonedResponse?.surveyAnswerList || []).map((item, index) => {
          if (item.type === 'multipleAnswer') {
            // todo: ask the type of any
            (clonedResponse?.surveyAnswerList[index] as any).subAnswerList = [
              ...(
                clonedResponse?.surveyAnswerList[
                  index
                ] as IMultipleAnswerAnswerView
              ).subAnswerList,
              ...generateSubAnswerList(),
            ];
          }
        });

        handleUpdateAddResponse(
          'update',
          clonedResponse as ISurveyResponseView,
          surveyItem.current as ISurveyItem
        );
        setResponseItem(clonedResponse);
      } else {
        // create
        const newResponse: ISurveyResponseView = {
          draft: false,
          title: '',
          tags: [],
          surveyId: surveyItem.current!.surveyId,
          surveyAnswerList: [...generateAnswerList()] as Array<IAnswersView>,
          effectiveDate: new Date().getTime(),
          surveyVersion: survey.current?.currentVersion as string,
          instance: hospital?.hospitalInitialInformation.chatHistoryItemId,
          extraInfo: {
            itemId: hospital?.hospitalInitialInformation?.chatHistoryItemId,
            providerId: providerId,
          },
        };

        handleUpdateAddResponse(
          'add',
          newResponse,
          surveyItem.current as ISurveyItem
        );
      }
      setResponseCounter((prevState) => prevState + 2);
    }
  }, [questionList.length]);

  const handleChangeRating = (selectedQuestionKey: string, rating: 1 | 0) => {
    setQuestionList((prevState: IQuestionListItem[]) => {
      return prevState.map((el) => {
        if (el.key === selectedQuestionKey) {
          return {
            ...el,
            rating: rating,
          };
        }
        return el;
      });
    });

    const userRateKey = generateKeys('userrate');

    const clonedResponse = cloneDeep(responseItem);
    (clonedResponse?.surveyAnswerList || []).map((item, ind) => {
      if (item.type === 'multipleAnswer') {
        // todo: ask the type of any
        (clonedResponse?.surveyAnswerList[ind] as any).subAnswerList = [
          ...(
            clonedResponse?.surveyAnswerList[ind] as IMultipleAnswerAnswerView
          ).subAnswerList.map((el) => {
            if (el.ref === selectedQuestionKey) {
              return {
                ...el,
                values: [
                  ...(el.values || []).filter((val) => val.key !== userRateKey),
                  { key: userRateKey, value: rating.toString() },
                ],
              };
            }
            return el;
          }),
        ];
      }
    });

    handleUpdateAddResponse(
      'update',
      clonedResponse as ISurveyResponseView,
      surveyItem.current as ISurveyItem
    );

    setResponseItem(clonedResponse);
  };

  const setErrorMessageAndRestForm = (message: string) => {
    setQuestionList((prevVal) => [
      ...prevVal,
      {
        threadId: providerId.toString(),
        title: question.content,
        ans: message,
        annotations: [],
        rating: null,
        key: '',
      },
    ]);
    setQuestion({
      content: '',
      streamAnswer: '',
      isLoading: false,
    });
  };
  const pauseMessage = (message: string) => {
    listeningForResponse.current = false;
    abortControllerRef.current?.abort();
    setErrorMessageAndRestForm(message);
  };

  const handleAddMultipleAnnotations = async (
    annotationIdsList: Array<string>
  ) => {
    try {
      const data = await appDispatch(
        requireProviderDocumentsByExtraIds({
          documentIds: annotationIdsList.map((item) => item),
        })
      );
      return data.items;
    } catch (error) {
      console.error('Failed to fetch citation:', error);
    }
  };

  const handleShowCitation = async (jsonData: any) => {
    let annotations = {};
    const annotationsListIds: string[] = [];
    const annotationsList = jsonData.item.content[0].annotations;
    annotationsList.map((annotation: any) => {
      annotationsListIds.push(annotation?.file_id);
    });

    if (annotationsListIds.length > 0) {
      annotations = await handleAddMultipleAnnotations(annotationsListIds);
    }
    return annotations;
  };

  const sendMessageWithGPT5 = async (
    currentQuestion: string,
    questionListHistory: IQuestionListItem[]
  ) => {
    const abortController = new AbortController();

    abortControllerRef.current = abortController;
    setQuestion((prevVal) => ({ ...prevVal, isLoading: true }));

    let buffer = '';
    listeningForResponse.current = true;
    let annotations = {};
    let showCitationFlag = false;

    try {
      const messageList = questionListHistory.reduce(
        (
          acc: Array<{
            role: 'user' | 'assistant';
            content: Array<{ [key: string]: string }>;
          }>,
          el: IQuestionListItem
        ) => {
          acc.push({
            role: 'user',
            content: [{ type: 'input_text', text: el.title }],
          });
          acc.push({
            role: 'assistant',
            content: [{ type: 'output_text', text: el.ans }],
          });
          return acc;
        },
        []
      );

      messageList.push({
        role: 'user',
        content: [{ type: 'input_text', text: currentQuestion }],
      });

      // define instruction
      const instruction = [
        {
          role: 'developer',
          content: [{ type: 'input_text', text: hospital?.instruction || '' }],
        },
      ];

      const runResponse = await fetch(BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model:
            hospital?.hospitalInitialInformation.botProperties.model ||
            'gpt-5-nano',
          input: [...instruction, ...messageList],
          stream: true,
          text: {
            format: { type: 'text' },
            verbosity:
              hospital?.hospitalInitialInformation.botProperties.verbosity ||
              'medium',
          },
          reasoning: {
            // effort:
            //   hospital?.hospitalInitialInformation.botProperties
            //     .reasoningEffort || 'medium',
          },
          tools: [
            {
              type: 'file_search',
              vector_store_ids: [
                hospital?.hospitalInitialInformation.botProperties
                  .vectorStoreId,
              ],
            },
          ],
          store: true,
          include: [],
        }),
        signal: abortController.signal,
      });

      if (!runResponse.ok) {
        throw new Error(`HTTP error! status: ${runResponse.status}`);
      }

      const reader = runResponse.body?.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let remaining = '';

      while (!done && listeningForResponse.current) {
        const { value, done: readerDone } = (await reader?.read()) as any;
        const decodedChunkCandid = decoder.decode(value, { stream: true });

        const completeValueIndex = decodedChunkCandid.lastIndexOf('\n');
        const decodedChunk =
          remaining + decodedChunkCandid.slice(0, completeValueIndex);
        remaining = decodedChunkCandid.slice(
          completeValueIndex,
          decodedChunkCandid.length
        );

        try {
          const dataList = [...decodedChunk.matchAll(/data:\s?(\{.*\})/g)];

          for (const item of dataList) {
            try {
              const jsonData = JSON.parse(item[1]);

              if (jsonData?.type === 'response.output_text.delta') {
                // Handle streamed data
                const text = jsonData.delta;

                if (
                  !hospital!.hospitalInitialInformation.showCitation &&
                  text.matchAll(/【.*】?/g).length > 0 &&
                  !showCitationFlag
                ) {
                  if (!text.trim().endsWith('】')) showCitationFlag = true;
                  continue;
                } else if (showCitationFlag) {
                  if (text.trim().endsWith('】')) {
                    showCitationFlag = false;
                    buffer += ' ';
                  }
                  continue;
                } else {
                  buffer += text || '';
                }

                if (buffer.length > 0) {
                  setQuestion((prevVal) => ({
                    ...prevVal,
                    streamAnswer: buffer,
                    isLoading: false,
                  }));
                }

                if (question.streamAnswer !== buffer) {
                  setQuestion((prevVal) => ({
                    ...prevVal,
                    streamAnswer: buffer,
                    isLoading: false,
                  }));
                }
              }

              if (
                jsonData?.type === 'response.output_item.done' &&
                jsonData?.item.content
              ) {
                // Handle final response
                if (hospital?.hospitalInitialInformation.showCitation) {
                  annotations = await handleShowCitation(jsonData);
                }

                const botKey = `${Math.round(Math.random() * 1_000_000)}`;
                setQuestionList((prevVal) => [
                  ...prevVal,
                  {
                    threadId: providerId.toString(),
                    title: question.content,
                    ans: buffer,
                    annotations: annotations,
                    rating: null,
                    key: botKey,
                  },
                ]);

                setQuestion({
                  content: '',
                  streamAnswer: '',
                  isLoading: false,
                });
                setFetchingData(false);
              }
            } catch (error) {
              console.error(error);
            }
          }
        } catch (error) {
          // console.error('Error parsing chunk:', error);
          if (listeningForResponse.current)
            setErrorMessageAndRestForm(t('errorInFetchingResponse'));
          setFetchingData(false);
        }

        done = readerDone;
      }
    } catch (error) {
      if (listeningForResponse.current)
        setErrorMessageAndRestForm(t('errorInFetchingResponse'));
      // console.error('Error in fetching or processing data:', error);
    } finally {
      setFetchingData(false);
      setQuestion((prevVal) => ({ ...prevVal, isLoading: false }));
    }
  };

  useEffect(() => {
    if (question.content !== '') {
      if (!isRunningBot.current && providerId && !thread) {
        setThread(providerId.toString());
      }
      if (thread) {
        setFetchingData(true);
        sendMessageWithGPT5(question.content, questionList);
      }
    }
  }, [question.content, thread]);

  useEffect(() => {
    if (fetchingData) {
      timer = setTimeout(() => {
        pauseMessage(t('errorInFetchingResponse'));
        setFetchingData(false);
      }, 120_000);
      return () => clearTimeout(timer);
    }
    return;
  }, [fetchingData]);

  return (
    <cntx.Provider
      value={{
        question,
        setQuestion,
        questionList,
        pauseMessage,
        handleChangeRating,
      }}
    >
      {children}
    </cntx.Provider>
  );
};

export function useQuestionContext() {
  return useContext(cntx);
}

export default ContextProvider;
